import React from "react";
import { motion } from "framer-motion";
import { ABOUT } from "@/constants/testIds";
import RevealLines from "@/components/RevealLines";

export default function About() {
  return (
    <section
      id="about"
      data-testid={ABOUT.section}
      className="relative py-28 md:py-40"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="sticky top-32"
          >
            <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
              (01) &nbsp; About
            </span>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-[1.05] text-foreground">
              A quiet room, a steady presence.
            </h2>
            <p className="mt-6 text-sm text-muted-foreground max-w-sm leading-relaxed">
              Twelve years of listening — to children, adolescents, adults
              — held in a psychoanalytic tradition, softened by warmth.
            </p>
          </motion.div>
        </div>

        <div className="lg:col-span-8 space-y-8">
          <RevealLines
            as="h3"
            className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.15] text-foreground"
            lines={[
              "Some feelings are known to us.",
              "Others leak out sideways —",
              <span key="s" className="text-primary italic">in what we can&rsquo;t say.</span>,
            ]}
            stagger={0.14}
            duration={1}
          />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-6"
          >
            <p className="text-base md:text-lg leading-relaxed text-foreground/85">
              Neha Gupta is a psychodynamic psychotherapist based in Panjim,
              Goa, working in independent practice. Her work sits at
              the meeting point of the deeply personal and the quietly
              universal — the ordinary feelings and relationships that
              shape every life, whether we notice them or not.
            </p>
            <p className="text-base md:text-lg leading-relaxed text-foreground/75">
              She holds an MSc in Child &amp; Adolescent Mental Health and
              Psychological Practice from the University of Edinburgh, and
              is psychoanalytically trained at the Psychoanalytic Training
              and Research Centre (PTRC), Mumbai. Beyond her formal
              training, what steadies her practice is a rare, intuitive
              empathy for the emotional worlds of the people she meets.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1.2, delay: 0.3 }}
            className="pt-8 border-t border-border grid grid-cols-2 gap-6"
          >
            <Stat n="12+" label="Years in practice" />
            <Stat n="6+" label="Years with adults" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="font-serif text-4xl md:text-5xl text-primary">{n}</div>
      <div className="mt-2 text-xs uppercase tracking-[0.2em] text-muted-foreground leading-relaxed">
        {label}
      </div>
    </div>
  );
}
